import React, { useMemo } from 'react';
import { VblForecastResult, VblForecastConfiguration, VblForecastFilters } from '@/types/vblForecast';
import { TableCard } from '@/components/ui';
import { CalendarIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline';

interface VblForecastGanttChartProps {
  results: VblForecastResult[];
  configuration: VblForecastConfiguration;
  filters: VblForecastFilters;
}

export const VblForecastGanttChart: React.FC<VblForecastGanttChartProps> = ({
  results,
  configuration,
  filters
}) => {
  const ganttData = useMemo(() => {
    // Filter out records by filter
    let filtered = results.filter(r => r.hasMapping && r.predictedFailureDate);
    // Apply user filters
    if (filters.sites.length > 0) {
      filtered = filtered.filter(result => filters.sites.includes(result.turbine.site_name));
    }
    if (filters.turbineModels.length > 0) {
      filtered = filtered.filter(result => filters.turbineModels.includes(result.turbine.model_name));
    }
    if (filters.componentsMajor.length > 0) {
      filtered = filtered.filter(result => filters.componentsMajor.includes(result.turbine.major_component_name || ''));
    }
    if (filters.componentsMinor.length > 0) {
      filtered = filtered.filter(result => filters.componentsMinor.includes(result.turbine.component_name || ''));
    }

    const mappedResults = filtered;

    if (mappedResults.length === 0) {
      return { timelineData: [], timeScale: { min: new Date(), max: new Date(), range: 0 } };
    }

    // Calculate time scale for the Gantt chart including detection dates
    const now = new Date();
    const allDates = mappedResults.flatMap(result => [
      new Date(result.turbine.detected_date), // Add detection dates
      new Date(result.predictedFailureDate),
      new Date(result.confidenceInterval.lower),
      new Date(result.confidenceInterval.upper)
    ]);

    const dataMinDate = new Date(Math.min(now.getTime(), ...allDates.map(d => d.getTime())));
    const maxDate = new Date(Math.max(...allDates.map(d => d.getTime())));

    // Extend the minimum date to the start of the containing period
    let minDate = new Date(dataMinDate);

    // Calculate time range in days to determine interval
    const preliminaryRangeInDays = (maxDate.getTime() - dataMinDate.getTime()) / (1000 * 60 * 60 * 24);

    // Round min date down to period start based on expected interval
    if (preliminaryRangeInDays <= 180) {
      // Monthly view - round down to start of month
      minDate = new Date(dataMinDate.getFullYear(), dataMinDate.getMonth(), 1);
    } else if (preliminaryRangeInDays <= 1095) {
      // Quarterly view - round down to start of quarter (using UTC)
      const quarter = Math.floor(dataMinDate.getMonth() / 3);
      const quarterStartMonth = quarter * 3;
      minDate = new Date(Date.UTC(dataMinDate.getFullYear(), quarterStartMonth, 1, 0, 0, 0, 0));
    } else {
      // Yearly view - round down to start of year
      minDate = new Date(dataMinDate.getFullYear(), 0, 1);
    }

    const timeRange = maxDate.getTime() - minDate.getTime();

    // Prepare timeline data sorted by predicted shutdown date
    const timelineData = mappedResults
      .map(result => {
        const predictedDate = new Date(result.predictedFailureDate);
        const lowerDate = new Date(result.confidenceInterval.lower);
        const upperDate = new Date(result.confidenceInterval.upper);
        const detectedDate = new Date(result.turbine.detected_date);

        return {
          turbine: result.turbine,
          predictedDate,
          lowerDate,
          upperDate,
          detectedDate,
          riskLevel: result.riskLevel,
          lastInspectionSeverity: result.turbine.inspection_severity,
          daysToFailure: Math.round(result.timeToFailure.days),
          // Calculate positions for Gantt bars
          predictedPosition: ((predictedDate.getTime() - minDate.getTime()) / timeRange) * 100,
          lowerPosition: ((lowerDate.getTime() - minDate.getTime()) / timeRange) * 100,
          upperPosition: ((upperDate.getTime() - minDate.getTime()) / timeRange) * 100,
          detectedPosition: ((detectedDate.getTime() - minDate.getTime()) / timeRange) * 100,
          confidenceWidth: ((upperDate.getTime() - lowerDate.getTime()) / timeRange) * 100,
          degradationWidth: ((predictedDate.getTime() - detectedDate.getTime()) / timeRange) * 100
        };
      })
      .sort((a, b) => a.daysToFailure - b.daysToFailure);

    return {
      timelineData,
      timeScale: { min: minDate, max: maxDate, range: timeRange }
    };
  }, [results, filters]);

  const getRiskColor = (riskLevel: string) => {
    switch (riskLevel) {
      case 'High': return { bg: 'bg-red-500', text: 'text-red-700', light: 'bg-red-100' };
      case 'Medium': return { bg: 'bg-yellow-500', text: 'text-yellow-700', light: 'bg-yellow-100' };
      case 'Low': return { bg: 'bg-green-500', text: 'text-green-700', light: 'bg-green-100' };
      default: return { bg: 'bg-gray-500', text: 'text-gray-700', light: 'bg-gray-100' };
    }
  };

  const getSeverityColor = (severityName: string) => {
    switch (severityName.toLowerCase()) {
      case 'critical': return { bg: 'bg-red-500', text: 'text-red-700', light: 'bg-red-100' };
      case 'warning': return { bg: 'bg-yellow-500', text: 'text-yellow-700', light: 'bg-yellow-100' };
      case 'good':
      case 'progressed':
        return { bg: 'bg-green-500', text: 'text-green-700', light: 'bg-green-100' };
      default: return { bg: 'bg-gray-500', text: 'text-gray-600', light: 'bg-gray-100' };
    }
  };

  const formatDate = (date: Date) => {
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const generateTimeMarkers = () => {
    const { min, max } = ganttData.timeScale;
    const markers: Array<{ date: Date; position: number; label: string }> = [];

    // Calculate time range in days
    const rangeInDays = (max.getTime() - min.getTime()) / (1000 * 60 * 60 * 24);

    // Determine appropriate interval based on range
    let interval: 'day' | 'week' | 'month' | 'quarter' | 'year';
    const maxMarkers = 12; // Increased to allow more markers

    if (rangeInDays <= 30) {
      interval = 'week';
    } else if (rangeInDays <= 180) { // Changed from 90 to 180
      interval = 'month';
    } else if (rangeInDays <= 730) { // 2 years - use monthly instead of quarterly
      interval = 'month';
    } else if (rangeInDays <= 1095) { // 3 years
      interval = 'quarter';
    } else {
      interval = 'year';
    }

    let current = new Date(min);

    // Round to the appropriate interval boundary
    if (interval === 'month') {
      // Move to the first of the next month if not already on the first
      if (current.getDate() !== 1) {
        current = new Date(current.getFullYear(), current.getMonth() + 1, 1);
      }
    } else if (interval === 'quarter') {
      const minYear = min.getUTCFullYear();
      const minMonth = min.getUTCMonth();
      const minQuarter = Math.floor(minMonth / 3);
      const quarterStartMonth = minQuarter * 3;
      current = new Date(Date.UTC(minYear, quarterStartMonth, 1, 0, 0, 0, 0));
    } else if (interval === 'year') {
      // Move to the next year if not already Jan 1
      if (current.getMonth() !== 0 || current.getDate() !== 1) {
        current = new Date(current.getFullYear() + 1, 0, 1);
      }
    }

    let markerCount = 0;

    while (current <= max && markerCount < maxMarkers) {
      const position = ((current.getTime() - min.getTime()) / ganttData.timeScale.range) * 100;

      // Only add marker if it's within the visible range
      if (position >= 0 && position <= 100) {
        // Format label based on interval
        let label: string;
        if (interval === 'year') {
          label = current.getFullYear().toString();
        } else if (interval === 'quarter') {
          const quarter = Math.floor(current.getUTCMonth() / 3) + 1;
          label = `Q${quarter} ${current.getUTCFullYear()}`;

        } else if (interval === 'month') {
          label = current.toLocaleDateString('en-US', { month: 'short', year: '2-digit' });
        } else {
          label = current.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
        }

        markers.push({
          date: new Date(current),
          position,
          label
        });
        markerCount++;
      }

      // Increment based on interval
      if (interval === 'year') {
        current.setFullYear(current.getFullYear() + 1);
      } else if (interval === 'quarter') {
        // Use UTC methods for consistency
        current.setUTCMonth(current.getUTCMonth() + 3);
      } else if (interval === 'month') {
        current.setMonth(current.getMonth() + 1);  // Simple 1 month increment
      } else if (interval === 'week') {
        current.setDate(current.getDate() + 7);
      } else {
        current.setDate(current.getDate() + 1);
      }
    }

    return markers;
  };

  if (ganttData.timelineData.length === 0) {
    return (
      <TableCard
        title="Shutdown Timeline Gantt Chart"
        subtitle="Visual timeline of predicted shutdown dates with confidence intervals"
      >
        <div className="p-8 text-center">
          <CalendarIcon className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-2 text-sm font-medium text-gray-900">No Forecast Data</h3>
          <p className="mt-1 text-sm text-gray-500">
            No turbines with forecasts available to display in timeline
          </p>
        </div>
      </TableCard>
    );
  }

  const timeMarkers = generateTimeMarkers();

  return (
    <TableCard
      title="Shutdown Timeline Gantt Chart"
      subtitle={`Timeline showing predicted shutdown dates with ${configuration.confidenceLevel * 100}% confidence intervals for ${ganttData.timelineData.length} turbines`}
    >
      <div className="p-6">
        {/* Time scale header - CSS Grid layout */}
        <div className="mb-4 grid grid-cols-[12rem_1fr_10rem_5rem] gap-2 items-center pr-4">
          {/* Turbine column header */}
          <div>
            <div className="text-xs font-medium text-gray-500 uppercase tracking-wider">
              Turbine
            </div>
          </div>

          {/* Timeline header */}
          <div className="relative">
            <div className="relative h-12 border-b border-gray-200">
              {/* Time markers */}
              {timeMarkers.map((marker, index) => {
                // Use different alignment logic for quarter vs other labels
                let transform = 'transform -translate-x-1/2'; // Default: center for non-quarter labels

                // Quarter labels are left-aligned to match background boundaries
                if (marker.label.startsWith('Q')) {
                  transform = ''; // No transform - left aligned at quarter start
                } else {
                  // Apply smart alignment to non-quarter labels
                  if (marker.position <= 10) {
                    transform = 'transform translate-x-0'; // Left align for labels near start
                  } else if (marker.position >= 90) {
                    transform = 'transform -translate-x-full'; // Right align for labels near end
                  }
                }

                return (
                  <div
                    key={index}
                    className={`absolute ${transform}`}
                    style={{ left: `${marker.position}%` }}
                  >
                    <div className="w-px h-4 bg-gray-300"></div>
                    <div className="text-xs mt-2 whitespace-nowrap font-medium text-gray-600">
                      {marker.label}
                    </div>
                  </div>
                );
              })}

              {/* Today marker in header */}
              {(() => {
                const now = new Date();
                const { min, max: _max, range } = ganttData.timeScale;
                const todayPosition = ((now.getTime() - min.getTime()) / range) * 100;

                if (todayPosition >= 0 && todayPosition <= 100) {
                  return (
                    <div
                      className="absolute bottom-0"
                      style={{ left: `${todayPosition}%` }}
                    >
                      <div className="text-xs text-blue-600 font-semibold whitespace-nowrap text-center -mb-5">
                        Today
                      </div>
                      <div className="w-0 h-0 border-l-2 border-r-2 border-t-4 border-transparent border-t-blue-600 -mb-1"></div>
                    </div>
                  );
                }
                return null;
              })()}

            </div>
          </div>

          {/* Days to Estimated Shutdown column header */}
          <div className="text-right">
            <div className="text-xs font-medium text-gray-500 uppercase tracking-wider">
              Days to Estimated Shutdown
            </div>
          </div>

          {/* Risk column header */}
          <div>
            <div className="text-xs font-medium text-gray-500 uppercase tracking-wider">
              Risk & Last Inspection Severity
            </div>
          </div>
        </div>


        {/* Gantt chart rows */}
        <div className="space-y-2 max-h-96 overflow-y-scroll" style={{ scrollbarGutter: 'stable' }}>
          {ganttData.timelineData.map((item, index) => {
            const colors = getRiskColor(item.riskLevel);
            const sevColours = item.lastInspectionSeverity ? getSeverityColor(item.lastInspectionSeverity) : { bg: 'bg-gray-500', text: 'text-gray-700', light: 'bg-gray-100' };

            return (
              <div key={index} className="grid grid-cols-[12rem_1fr_10rem_5rem] gap-4 items-center py-1">
                {/* Turbine info */}
                <div className="flex justify-between items-start gap-2">
                  {/* Left side - Turbine and Site */}
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium text-gray-900 truncate">
                      {item.turbine.turbine_name}
                    </div>
                    <div className="text-xs text-gray-400 truncate">
                      {item.turbine.site_name}
                    </div>
                  </div>

                  {/* Right side - Components */}
                  <div className="flex-1 min-w-0">
                    <div className="text-xs font-semibold text-gray-700 truncate">
                      {item.turbine.major_component_name || 'N/A'}
                    </div>
                    {item.turbine.component_name && (
                      <div className="text-xs text-gray-500 truncate">
                        {item.turbine.component_name}
                      </div>
                    )}
                  </div>
                </div>

                {/* Gantt bar container */}
                <div className="relative">
                  <div className="h-8 bg-gray-50 rounded relative border">
                    {/* Quarter background regions for consistency */}
                    {(() => {
                      const quarterRegions = [];
                      const { min, max: _max, range } = ganttData.timeScale;

                      // Generate quarter backgrounds
                      const minYear = min.getUTCFullYear();
                      const minMonth = min.getUTCMonth();
                      const minQuarter = Math.floor(minMonth / 3);

                      let currentYear = minYear;
                      let currentQuarter = minQuarter;
                      let quarterIndex = 0; // For alternating colors

                      // eslint-disable-next-line no-constant-condition
                      while (true) {
                        const quarterStart = new Date(Date.UTC(currentYear, currentQuarter * 3, 1, 0, 0, 0, 0));
                        const quarterEnd = new Date(Date.UTC(currentYear, (currentQuarter + 1) * 3, 1, 0, 0, 0, 0));

                        const startPos = ((quarterStart.getTime() - min.getTime()) / range) * 100;
                        const endPos = ((quarterEnd.getTime() - min.getTime()) / range) * 100;

                        // Stop if we're beyond the visible range
                        if (startPos > 100) break;

                        // Only show if within visible range
                        if (endPos > 0 && startPos < 100) {
                          const visibleStart = Math.max(0, startPos);
                          const visibleEnd = Math.min(100, endPos);
                          const width = visibleEnd - visibleStart;

                          if (width > 0) {
                            quarterRegions.push(
                              <div
                                key={`row-quarter-${currentYear}-${currentQuarter}`}
                                className={`absolute h-full ${quarterIndex % 2 === 0 ? 'bg-gray-50' : 'bg-white'}`}
                                style={{
                                  left: `${visibleStart}%`,
                                  width: `${width}%`
                                }}
                              />
                            );
                          }
                        }

                        // Move to next quarter
                        quarterIndex++;
                        currentQuarter++;
                        if (currentQuarter >= 4) {
                          currentQuarter = 0;
                          currentYear++;
                        }
                      }

                      return quarterRegions;
                    })()}

                    {/* Degradation phase bar (detection to prediction) */}
                    <div
                      className="absolute h-3 top-2.5 bg-orange-200 border border-orange-300 rounded"
                      style={{
                        left: `${item.detectedPosition}%`,
                        width: `${item.degradationWidth}%`
                      }}
                    />

                    {/* Confidence interval bar */}
                    <div
                      className={`absolute h-6 top-1 rounded border border-gray-300`}
                      style={{
                        left: `${item.lowerPosition}%`,
                        width: `${item.confidenceWidth}%`,
                        backgroundColor: item.riskLevel === 'High' ? 'rgba(220, 38, 38, 0.15)' :
                          item.riskLevel === 'Medium' ? 'rgba(234, 179, 8, 0.15)' : 'rgba(22, 163, 74, 0.15)',
                        borderColor: item.riskLevel === 'High' ? 'rgba(220, 38, 38, 0.3)' :
                          item.riskLevel === 'Medium' ? 'rgba(234, 179, 8, 0.3)' : 'rgba(22, 163, 74, 0.3)'
                      }}
                    />

                    {/* Detection date marker */}
                    <div
                      className="absolute w-2 h-6 top-1 rounded bg-orange-500"
                      style={{ left: `${item.detectedPosition}%` }}
                    />

                    {/* Predicted shutdown marker */}
                    <div
                      className={`absolute w-2 h-6 top-1 rounded ${colors.bg}`}
                      style={{ left: `${item.predictedPosition}%` }}
                    />
                  </div>
                </div>

                {/* Failure info */}
                <div className="text-right">
                  <div className="text-sm font-medium text-gray-900">
                    {item.daysToFailure} days
                  </div>
                  <div className="text-xs text-gray-500">
                    {formatDate(item.predictedDate)}
                  </div>
                  <div className="text-xs text-orange-600">
                    Detected: {formatDate(item.detectedDate)}
                  </div>
                </div>
                <div className="flex justify-between items-start gap-2">
                  {/* Risk level */}
                  <div>
                    <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${colors.text} ${colors.light}`}>
                      {item.riskLevel === 'High' && (
                        <ExclamationTriangleIcon className="w-3 h-3 mr-1" />
                      )}
                      {item.riskLevel}
                    </span>
                  </div>

                  {/* Last Inspection Severity*/}
                  {item.lastInspectionSeverity && (<div>
                    <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${sevColours.text} ${sevColours.light}`}>
                      {item.lastInspectionSeverity === 'Critical' && (
                        <ExclamationTriangleIcon className="w-3 h-3 mr-1" />
                      )}
                      {item.lastInspectionSeverity}
                    </span>
                  </div>)}
                </div>
              </div>
            );
          })}
        </div>

        {/* Legend */}
        <div className="mt-6 pt-4 border-t border-gray-200">
          <div className="flex items-center justify-between text-xs text-gray-500">
            <div className="flex items-center space-x-4">
              <div className="flex items-center space-x-2">
                <div className="w-2 h-6 bg-orange-500 rounded"></div>
                <span>Detection Date</span>
              </div>
              <div className="flex items-center space-x-2">
                <div className="flex space-x-1">
                  <div className="w-2 h-6 bg-red-500 rounded"></div>
                  <div className="w-2 h-6 bg-yellow-500 rounded"></div>
                  <div className="w-2 h-6 bg-green-500 rounded"></div>
                </div>
                <span>Predicted Shutdown</span>
              </div>
              <div className="flex items-center space-x-2">
                <div className="w-8 h-2 bg-orange-200 border border-orange-300 rounded"></div>
                <span>Degradation Phase</span>
              </div>
              <div className="flex items-center space-x-2">
                <div className="flex flex-col space-y-1">
                  <div className="w-8 h-2 bg-red-100 border border-red-200 rounded"></div>
                  <div className="w-8 h-2 bg-yellow-100 border border-yellow-200 rounded"></div>
                  <div className="w-8 h-2 bg-green-100 border border-green-200 rounded"></div>
                </div>
                <span>{configuration.confidenceLevel * 100}% Confidence Interval</span>
              </div>
            </div>

            <div className="text-xs text-gray-400">
              Sorted by shutdown urgency (shortest time first)
            </div>
          </div>
        </div>

      </div>
    </TableCard>
  );
};